import { FilterSelection } from '@enamelai/design-system'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { ICallDirection, ICallRecording, ICalls } from '@dataTypes/calls.type'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { areValuesEqual } from '@utils/settings.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  fetchCallRecordingSettingsApi,
  saveCallRecordingSettingsApi,
} from '@services/api/call-recording-settings.api'
import { deleteCallRecording } from '@store/actions/calls.action'
import {
  getBulkPresignedUrl,
  getCallRecordings,
  streamCallRecording,
} from '@services/api/calls.api'
import {
  needsRecordingCanonicalize,
  recordingBlobNameFromRef,
  triggerRecordingDownload,
} from '@utils/call-recording-playback.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { getFullName } from '@utils/fullname.util'

const LOADING_CONTAINER = 'settings-call-recording'

const dtoToForm = (dto: ICallRecording): ICallRecording => ({
  clinic_id: dto.clinic_id,
  inbound_calls: dto.inbound_calls,
  outbound_calls: dto.outbound_calls,
  internal_calls: dto.internal_calls ?? false,
  auto_start: dto.auto_start,
  play_tone: dto.play_tone,
  pause_recording_on_hold: dto.pause_recording_on_hold,
  retention_period: dto.retention_period,
  retention_period_unit: dto.retention_period_unit,
  encryption: dto.encryption,
})

const mapFilterTypeToApi = (
  label: string | undefined,
): ICallDirection | undefined => {
  if (label === 'Inbound') return ICallDirection.Incoming
  if (label === 'Outbound') return ICallDirection.Outgoing
  return undefined
}

const mergePresignedUrls = async (rows: ICalls[]): Promise<ICalls[]> => {
  const need = rows.filter(
    (c) => Boolean(c.call_sid) && needsRecordingCanonicalize(c.recording_url),
  )
  if (need.length === 0) return rows
  try {
    const res = await getBulkPresignedUrl(need)
    if (res.statusCode !== 200 || !res.data?.calls) return rows
    const byId = new Map(
      (res.data.calls as ICalls[]).map((c) => [c.call_id, c] as const),
    )
    return rows.map((r) => {
      const u = byId.get(r.call_id)
      return u?.recording_url ? { ...r, recording_url: u.recording_url } : r
    })
  } catch {
    return rows
  }
}

const downloadRecordingFile = async (
  audioSrc: string,
  callId: string,
  onError?: (message: string) => void,
): Promise<void> => {
  if (!audioSrc) return
  const blobName = recordingBlobNameFromRef(audioSrc)
  let objectUrl = ''
  try {
    if (blobName) {
      objectUrl = await streamCallRecording(blobName)
      triggerRecordingDownload(objectUrl, callId)
      return
    }
    const response = await fetch(audioSrc, {
      mode: 'cors',
      credentials: 'omit',
    })
    if (!response.ok) throw new Error(String(response.status))
    const buffer = await response.arrayBuffer()
    const audioBlob = new Blob([buffer], { type: 'audio/mpeg' })
    objectUrl = URL.createObjectURL(audioBlob)
    triggerRecordingDownload(objectUrl, callId)
  } catch (e) {
    onError?.('Could not download recording. Try playing it first.')
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  }
}

export interface ICallRecordingHooks {
  data: ICallRecording
  handleChange: (
    key: keyof ICallRecording,
    value: boolean | number | string,
  ) => void
  handleReset: () => void
  handleSave: () => Promise<void>
  hasChanges: boolean
  savedBaseline: ICallRecording
  mainFilters: FilterSelection
  mainSearchQuery: string
  recordings: ICalls[]
  setHeaderButtons: Dispatch<SetStateAction<ReactNode>>
  setMainFilters: Dispatch<SetStateAction<FilterSelection>>
  setMainSearchQuery: Dispatch<SetStateAction<string>>
  loading: boolean
  dataReady: boolean
  pendingDelete: ICalls | null
  setPendingDelete: Dispatch<SetStateAction<ICalls | null>>
  confirmDelete: () => Promise<void>
  onDownload: (call: ICalls) => void
  showUnsavedModal: boolean
  setShowUnsavedModal: Dispatch<SetStateAction<boolean>>
  onConfirmDiscardNavigation: () => void
  onCancelDiscardNavigation: () => void
  staffDisplay: (call: ICalls) => string
}

function readUserIdFromStorage(): string {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const u = JSON.parse(raw) as { user_id?: string }
    return u.user_id ?? ''
  } catch {
    return ''
  }
}

const useCallRecording = (): ICallRecordingHooks => {
  const dispatch = useAppDispatch()
  const { setHeaderButtons } = useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const userId = useMemo(() => readUserIdFromStorage(), [])

  const [savedBaseline, setSavedBaseline] = useState<ICallRecording>({
    clinic_id: '',
    inbound_calls: true,
    outbound_calls: true,
    internal_calls: false,
    auto_start: true,
    play_tone: true,
    pause_recording_on_hold: false,
    retention_period: 10,
    retention_period_unit: 'year/s',
    encryption: true,
  })
  const [data, setData] = useState<ICallRecording>(savedBaseline)
  const [recordings, setRecordings] = useState<ICalls[]>([])
  const [mainSearchQuery, setMainSearchQuery] = useState('')
  const [mainFilters, setMainFilters] = useState<FilterSelection>({})
  const [loading, setLoading] = useState(false)
  const [settingsReady, setSettingsReady] = useState(false)
  const [recordingsReady, setRecordingsReady] = useState(false)
  const settingsGen = useRef(0)
  const recordingsGen = useRef(0)
  const [pendingDelete, setPendingDelete] = useState<ICalls | null>(null)
  const pendingDeleteRef = useRef<ICalls | null>(null)
  pendingDeleteRef.current = pendingDelete
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)

  const clinicId = selectedClinic?.clinic_id ?? ''

  useClinicChange(clinicId, () => {
    settingsGen.current += 1
    recordingsGen.current += 1
    setSettingsReady(false)
    setRecordingsReady(false)
    setLoading(true)
    setRecordings([])
  })

  const dataReady = settingsReady && recordingsReady

  useSettingsSubpageLoading(!dataReady)

  const hasChanges = useMemo(() => {
    return !areValuesEqual(
      [
        data.inbound_calls,
        data.outbound_calls,
        data.auto_start,
        data.play_tone,
        data.pause_recording_on_hold,
        data.retention_period,
        data.retention_period_unit,
        data.encryption,
      ],
      [
        savedBaseline.inbound_calls,
        savedBaseline.outbound_calls,
        savedBaseline.auto_start,
        savedBaseline.play_tone,
        savedBaseline.pause_recording_on_hold,
        savedBaseline.retention_period,
        savedBaseline.retention_period_unit,
        savedBaseline.encryption,
      ],
    )
  }, [data, savedBaseline])

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const loadSettings = useCallback(async (): Promise<void> => {
    const gen = ++settingsGen.current
    if (!clinicId) {
      setLoading(false)
      setSettingsReady(true)
      return
    }
    setLoading(true)
    try {
      const res = await fetchCallRecordingSettingsApi(clinicId)
      if (res.statusCode !== 200 || !res.data?.call_recording_settings) {
        showToast(
          createFailedToast(
            'Call recording',
            res.message || 'Failed to load settings',
          ),
        )
        return
      }
      const mapped = dtoToForm(res.data.call_recording_settings)
      setSavedBaseline(mapped)
      setData(mapped)
    } catch {
      showToast(
        createFailedToast(
          'Call recording',
          'Failed to load call recording settings',
        ),
      )
    } finally {
      if (settingsGen.current === gen) {
        setLoading(false)
        setSettingsReady(true)
      }
    }
  }, [showToast, clinicId])

  const loadRecordings = useCallback(async (): Promise<void> => {
    const gen = ++recordingsGen.current
    if (!clinicId) {
      setRecordingsReady(true)
      return
    }
    try {
      const type = mapFilterTypeToApi(mainFilters.type?.[0])
      const res = await getCallRecordings({
        clinic_id: clinicId,
        days: 5,
        search: mainSearchQuery.trim() || undefined,
        type,
      })
      const raw = (res.data?.calls ?? []) as ICalls[]
      const merged = await mergePresignedUrls(raw)
      setRecordings(merged)
    } catch {
      setRecordings([])
    } finally {
      if (recordingsGen.current === gen) setRecordingsReady(true)
    }
  }, [clinicId, mainFilters, mainSearchQuery])

  useEffect(() => {
    void loadSettings()
  }, [loadSettings])

  useEffect(() => {
    void loadRecordings()
  }, [loadRecordings])

  const handleChange = (
    key: keyof ICallRecording,
    value: boolean | number | string,
  ): void => {
    setData((prev) => ({ ...prev, [key]: value }))
  }

  const handleReset = useCallback((): void => {
    setData({ ...savedBaseline })
  }, [savedBaseline])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const res = await saveCallRecordingSettingsApi({
        clinic_id: clinicId,
        inbound_calls: data.inbound_calls,
        outbound_calls: data.outbound_calls,
        internal_calls: data.internal_calls,
        auto_start: data.auto_start,
        play_tone: data.play_tone,
        pause_recording_on_hold: data.pause_recording_on_hold,
        retention_period: data.retention_period,
        retention_period_unit: data.retention_period_unit,
        encryption: data.encryption,
        updated_by: userId || null,
      })
      if (res.statusCode !== 200 || !res.data?.call_recording_settings) {
        showToast(
          createFailedToast('Call recording', res.message || 'Save failed'),
        )
        return
      }
      const mapped = dtoToForm(res.data.call_recording_settings)
      setSavedBaseline(mapped)
      setData(mapped)
      showToast(createSuccessToast('Saved', 'Call recording settings saved.'))
    } catch {
      showToast(createFailedToast('Call recording', 'Failed to save settings'))
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    showToast,
    clinicId,
    data.auto_start,
    data.encryption,
    data.inbound_calls,
    data.internal_calls,
    data.outbound_calls,
    data.pause_recording_on_hold,
    data.play_tone,
    data.retention_period,
    data.retention_period_unit,
    hideLoading,
    showLoading,
    userId,
  ])

  const confirmDelete = useCallback(async (): Promise<void> => {
    const toDelete = pendingDeleteRef.current
    if (!toDelete) return
    const id = toDelete.call_id
    setPendingDelete(null)
    showLoading({ container: LOADING_CONTAINER })
    try {
      await dispatch(deleteCallRecording(id)).unwrap()
      showToast(createSuccessToast('Deleted', 'Recording deleted.'))
      await loadRecordings()
    } catch {
      showToast(
        createFailedToast('Call recording', 'Failed to delete recording'),
      )
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [dispatch, showToast, hideLoading, loadRecordings, showLoading])

  const onDownload = useCallback(
    (call: ICalls): void => {
      if (!call.recording_url) return
      void downloadRecordingFile(
        call.recording_url,
        call.call_id,
        (message) => {
          showToast(createFailedToast('Download', message))
        },
      )
    },
    [showToast],
  )

  const staffDisplay = useCallback((call: ICalls): string => {
    const u = call.handled_by_user
    if (u) {
      const n = getFullName(
        {
          first_name: u.first_name ?? '',
          last_name: u.last_name ?? '',
        },
        true,
      )
      if (n.trim()) return n
      if (u.username) return u.username
    }
    return '—'
  }, [])

  const onConfirmDiscardNavigation = useCallback((): void => {
    setShowUnsavedModal(false)
    setData({ ...savedBaseline })
  }, [savedBaseline])

  const onCancelDiscardNavigation = useCallback((): void => {
    setShowUnsavedModal(false)
  }, [])

  return {
    data,
    handleChange,
    handleReset,
    handleSave,
    hasChanges,
    savedBaseline,
    mainFilters,
    mainSearchQuery,
    recordings,
    setHeaderButtons,
    setMainFilters,
    setMainSearchQuery,
    loading,
    dataReady,
    pendingDelete,
    setPendingDelete,
    confirmDelete,
    onDownload,
    showUnsavedModal,
    setShowUnsavedModal,
    onConfirmDiscardNavigation,
    onCancelDiscardNavigation,
    staffDisplay,
  }
}

export default useCallRecording
